import React, { useState } from 'react'
import Replay from '../Replay/Replay'
import { showError } from '../Toast/Toast'
import './ReplayImporter.css'

export default function ReplayImporter({ onBack }) {
  const [gameData, setGameData] = useState(null)
  const [importMethod, setImportMethod] = useState('paste') // 'paste' or 'file'
  const [jsonInput, setJsonInput] = useState('')
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)

  const parseGameData = (jsonString) => {
    try {
      const data = JSON.parse(jsonString)

      // Validate RiftChess format
      if (data.format !== 'riftchess' || !data.game || !data.moves) {
        throw new Error('Invalid RiftChess game format. Please ensure you have exported a game from RiftChess.')
      }

      // Convert to the format expected by Replay component
      const replayData = {
        id: data.game.id,
        white_player: data.game.white_player,
        black_player: data.game.black_player,
        result: data.game.result,
        time_control: data.game.time_control,
        rated: data.game.rated,
        white_rating_before: data.game.white_rating_before,
        black_rating_before: data.game.black_rating_before,
        white_rating_after: data.game.white_rating_after,
        black_rating_after: data.game.black_rating_after,
        started_at: data.game.started_at,
        completed_at: data.game.completed_at,
        board: data.final_state.board,
        red_squares: data.final_state.red_squares,
        blue_squares: data.final_state.blue_squares,
        green_squares: data.final_state.green_squares,
        visual_effects: data.final_state.visual_effects,
        moves: data.moves
      }

      return replayData
    } catch (err) {
      throw new Error(`Failed to parse game data: ${err.message}`)
    }
  }

  const handlePasteImport = () => {
    setLoading(true)
    setError(null)

    try {
      const parsedData = parseGameData(jsonInput)
      setGameData(parsedData)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleFileImport = (event) => {
    const file = event.target.files[0]
    if (!file) return

    setLoading(true)
    setError(null)

    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const parsedData = parseGameData(e.target.result)
        setGameData(parsedData)
      } catch (err) {
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }

    reader.onerror = () => {
      showError('Failed to read file')
      setLoading(false)
    }

    reader.readAsText(file)
  }

  const handleReset = () => {
    setGameData(null)
    setJsonInput('')
    setError(null)
    setImportMethod('paste')
  }

  if (gameData) {
    return (
      <Replay
        gameId={gameData.id}
        gameData={gameData}
        onBack={handleReset}
      />
    )
  }

  return (
    <div className="replay-importer-container">
      <div className="replay-importer-header">
        <div className="replay-logo">
          <img
            src="/images/logo/finallogosquare.png"
            alt="RiftChess"
            className="logo-icon"
          />
        </div>
        <div className="import-title">
          <h1>Import RiftChess Game</h1>
          <p>Import a previously exported RiftChess game to replay</p>
        </div>
      </div>

      <div className="import-content">
        <div className="import-methods">
          <div className="method-selector">
            <button
              className={`method-button ${importMethod === 'paste' ? 'active' : ''}`}
              onClick={() => setImportMethod('paste')}
            >
              Paste JSON
            </button>
            <button
              className={`method-button ${importMethod === 'file' ? 'active' : ''}`}
              onClick={() => setImportMethod('file')}
            >
              Upload File
            </button>
          </div>

          {importMethod === 'paste' && (
            <div className="paste-section">
              <label htmlFor="json-input" className="input-label">
                Paste RiftChess game JSON:
              </label>
              <textarea
                id="json-input"
                className="json-textarea"
                value={jsonInput}
                onChange={(e) => setJsonInput(e.target.value)}
                placeholder='Paste your exported RiftChess game JSON here...'
                rows={12}
              />
              <button
                className="import-button"
                onClick={handlePasteImport}
                disabled={!jsonInput.trim() || loading}
              >
                {loading ? 'Loading...' : 'Import Game'}
              </button>
            </div>
          )}

          {importMethod === 'file' && (
            <div className="file-section">
              <label htmlFor="file-input" className="input-label">
                Select RiftChess JSON file:
              </label>
              <input
                id="file-input"
                type="file"
                accept=".json"
                onChange={handleFileImport}
                className="file-input"
                disabled={loading}
              />
              {loading && <div className="loading-text">Loading game...</div>}
            </div>
          )}

          {error && (
            <div className="error-message">
              <p>{error}</p>
            </div>
          )}
        </div>

        <div className="import-info">
          <h3>How to get a RiftChess game file:</h3>
          <ol>
            <li>Play a game on RiftChess</li>
            <li>Go to the game's replay page</li>
            <li>Click "Download Game (JSON)" to save the file</li>
            <li>Upload that file here or copy its contents to replay the game</li>
          </ol>

          <div className="format-info">
            <p><strong>Supported format:</strong> RiftChess JSON exports only</p>
            <p>This preserves all special moves, timing, and visual effects.</p>
          </div>
        </div>
      </div>

      <div className="back-section">
        <button className="back-button" onClick={onBack}>
          Back to Dashboard
        </button>
      </div>
    </div>
  )
}